<template>
  <b-card>
    <b-card-body>
      <div class="overflow-x-auto">
        <div class="filter_Documentacion">
          <b-dropdown dropleft toggle-class="p-button-secondary btn-outline-secondary mx-2">
            <template #button-content>
              <PIcon icon="IconExcel" class="icon_PButton mr-2" />
              <span>{{ $t("action.export") }}</span>
            </template>
            <b-dropdown-item @click="downloadDocumentosTodos()">
              {{ $t("label.todos.documentos") }}
            </b-dropdown-item>
          </b-dropdown>

          <PSidebar
            v-model="mostrarFiltros"
            :title="$t('label.filtros')"
            icon="IconFilter"
            bVariant="secondary"
            :textButton="dameTextoBotonFiltros"
          >
            <documentacion-table-filters
              v-model="filtersDocumentos"
              :tiposDocumentos="tiposDocumentos"
              actionName="filtrar"
              :textActionName="$t('label.filtrar')"
              :maxRangeMonth="maxRangeMonthDate"
              @action="action"
              @cancel="action"
            />
          </PSidebar>
        </div>
        <QuickFilter :params.sync="params" @change="onChangeQuickFilter" />
        <div class="table-responsive">
          <hr />

          <b-table
            ref="tableDocumentos"
            :fields="visibleFields"
            :items="items"
            :sort-by.sync="params.orderBy"
            :sort-desc.sync="sortDesc"
            :empty-text="$t('label.noData')"
            no-border-collapse
            show-empty
            sort-icon-left
            @sort-changed="onSortingChange"
            responsive
            no-local-sorting
            borderless
          >
            <div class="text-center loader my-2" slot="table-busy">
              <b-spinner class="align-middle" />
            </div>
            <template #head(add-columns)>
              <select-columns :fields.sync="fields" :pagination="params" :cod-table="codTable" />
            </template>
            <template #cell(active)="row">
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="row.item.id"
                  v-model="row.item.active"
                />
                <label class="custom-control-label" :for="row.item.id" />
              </div>
            </template>
            <template #cell(documentType)="row">
              {{
                $te(`documento.tipo.${row.item.documentType?.toLowerCase()}`)
                  ? $t(`documento.tipo.${row.item.documentType?.toLowerCase()}`)
                  : row.item.documentType
              }}
            </template>
            <template #cell(actions-left)="row">
              <div class="table-button-container">
                <button
                  :id="`downloadFile-${row.index}`"
                  class="mx-1 my-auto"
                  @click="download(row.item)"
                >
                  <icon-download :height="16" :width="16" color="#000" />
                </button>
              </div>
            </template>
          </b-table>
        </div>
        <div class="mt-3" v-if="documentacionResponse && documentacionResponse.total > params.size">
          <pagination
            :current-page="params.page"
            :page-size="params.size"
            :total-elements="documentacionResponse.total"
            @change="onChangeCurrentPage"
          />
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script lang="ts">
import { Component, Prop } from "vue-property-decorator";
import Vue from "vue";
import {
  ICliente,
  IDelegacion,
  IDocumento,
  IField,
  IParametro,
  ISoConfColumnasUpdateDTO,
} from "@/entities";
import { Action, Getter } from "vuex-class";
import Pagination from "@/components/common/Pagination.vue";
import VueFilters from "@/vue-filters";

import SelectColumns from "@/components/common/SelectColumns/SelectColumns.vue";
import QuickFilter from "@/components/documentacion/common/QuickFilter.vue";
import { EDirection, IColumnaResponse } from "@/services";
import {
  IDocumentacionParams,
  IDocumentacionResponse,
} from "@/services/saldosonline/documentacion/documentacion.types";
import DocumentacionTableFilters from "@/components/documentacion/DocumentacionTableFilters.vue";
import format from "date-fns/format";
import VueMethods from "@/vue-methods";
import { Dictionary } from "vue-router/types/router";
import { IconDownload, IconTrash } from "@/components/look";
import { MAX_RANGE_MONTH_DATE } from "@/constans";

@Component({
  components: {
    DocumentacionTableFilters,
    IconDownload,
    IconTrash,
    SelectColumns,
    QuickFilter,
    Pagination,
    MAX_RANGE_MONTH_DATE,
  },
  mixins: [VueFilters, VueMethods],
})
export default class DocumentosTable extends Vue {
  @Getter("getClienteSelected") getClienteSelected!: ICliente;
  @Action("fetchDocumentacion")
  fetchDocumentos!: (params?: IDocumentacionParams) => Promise<IDocumentacionResponse | undefined>;
  @Action("saveColumna") saveColumna!: (
    params: ISoConfColumnasUpdateDTO
  ) => Promise<IColumnaResponse | undefined>;

  @Getter("getDelegacionesCliente") delegations!: IDelegacion[];

  @Prop({ required: false, default: [] })
  fieldsI!: IField[];

  @Prop({ required: true })
  params!: IDocumentacionParams;

  public maxRangeMonthDate = MAX_RANGE_MONTH_DATE;

  public filtersDocumentos: any = {
    fechaInicio: this.getStartDate(new Date().toISOString()),
    fechaFin: this.getEndDate(new Date().toISOString()),
    delegationsSelected: [],
    tipoDocumentoSelected: null,
  };

  public mostrarFiltros = false;
  public documentacionResponse?: IDocumentacionResponse | null = null;
  public codTable = "documentos";
  public sortDesc = true;

  beforeMount(): void {
    this.filtersDocumentos.fechaInicio = this.params.fechaInicio
      ? this.getStartDate(this.params.fechaInicio)
      : this.filtersDocumentos.fechaInicio;
    this.filtersDocumentos.fechaFin = this.params.fechaFin
      ? this.getEndDate(this.params.fechaFin)
      : this.filtersDocumentos.fechaFin;
    this.filtersDocumentos.tipoDocumentoSelected = this.params.tipoDocumento ?? null;

    this.sortDesc = this.params.direction === EDirection.DESC;
  }

  async loadInfo(): Promise<void> {
    const delegacionsSelected = this.delegations?.filter((d) =>
      this.params.delegaciones?.find((code) => code === d.codDelegacionSol)
    );

    this.params.delegaciones = delegacionsSelected.map((d) => d.codDelegacionSol);

    this.filtersDocumentos.delegationsSelected = delegacionsSelected.map((d) => d.id);

    await this.updateTiposDocumentos();
    await this.getDocumentacion();
  }

  async onChangeQuickFilter(): Promise<void> {
    await this.loadInfo();
  }

  get items(): IDocumento[] | undefined {
    return this.documentacionResponse?.listado;
  }

  public get visibleFields(): IField[] {
    return this.fieldsI.filter((item) => item.visible);
  }

  get fields(): IField[] {
    return JSON.parse(JSON.stringify(this.fieldsI));
  }

  set fields(fields: IField[]) {
    this.$emit("update:fieldsI", fields);
  }

  get dameTextoBotonFiltros(): string {
    let numFiltros = 0;
    Object.keys(this.filtersDocumentos).forEach((key: string) => {
      if (this.filtersDocumentos[key] != "" && this.filtersDocumentos[key] != null) {
        numFiltros = numFiltros + 1;
      }
    });

    return numFiltros ? `(${numFiltros}) ${this.$t("label.filtros")}` : this.$t("label.filtros");
  }

  tiposDocumentos: IParametro[] | undefined = [];

  async updateTiposDocumentos(): Promise<void> {
    this.tiposDocumentos = await this.$services.documentacion.fetchTiposDocumentosByPais(
      this.getClienteSelected?.pais?.id
    );
    this.params.tipoDocumento = this.tiposDocumentos?.find(
      (tipoDoc) => tipoDoc.codParametro == this.params.tipoDocumento
    )?.codParametro;

    this.filtersDocumentos.tipoDocumentoSelected = this.params.tipoDocumento ?? null;
  }

  onChangeCurrentPage(currentPage: number): void {
    this.params.page = currentPage;
    this.getDocumentacion();
  }

  onSortingChange(sort: { sortBy: string; sortDesc: boolean }): void {
    this.$nextTick(async () => {
      this.params.direction = sort.sortDesc ? EDirection.DESC : EDirection.ASC;
      await this.getDocumentacion();
      await this.saveColumnsConfig();
    });
  }

  async saveColumnsConfig(): Promise<void> {
    const params: ISoConfColumnasUpdateDTO = {
      codColumna: this.visibleFields
        .filter((f) => !f.aux)
        .map((f) => f.code)
        .toString(),
      codTabla: this.codTable,
      codTipoOrden: this.params.direction,
      numPagElements: this.params.size,
      codOrden: this.params.orderBy,
    };

    await this.saveColumna(params);
  }

  async getDocumentacion(): Promise<void> {
    this.$nextTick(() => {
      this.$router
        .replace({ path: this.$route.path, query: { ...this.params } as Dictionary<any> })
        .catch(() => {
          // Do nothing
        });
    });

    this.params.codCliente = this.getClienteSelected?.codCliente;

    this.documentacionResponse = await this.fetchDocumentos(this.params);
  }

  async download(doc: IDocumento): Promise<void> {
    const response = await this.$services.documentacion.downloadFile(doc);

    if (response) {
      this.downloadFile(response);
    } else {
      this.makeToast("danger", this.$t("label.documentation.download.error"));
    }
  }

  async downloadDocumentosTodos(): Promise<void> {
    if (this.items != undefined && this.items.length != 0) {
      const response = await this.$services.documentacion.downloadAllFiles(this.params);
      if (response) {
        this.downloadFile(response);
      } else {
        this.makeToast("danger", this.$t("label.documentation.download.error"));
      }
    }
  }

  action(action: { action: string; data?: any }): void {
    if (action.action === "download") {
      alert("download");
    } else if (action.action === "filtrar" || action.action === "clean") {
      this.params.page = 1;
      this.params.fechaInicio = format(new Date(this.filtersDocumentos.fechaInicio), "yyyy-MM-dd");
      this.params.fechaFin = format(new Date(this.filtersDocumentos.fechaFin), "yyyy-MM-dd");
      this.params.delegaciones =
        this.filtersDocumentos.delegationsSelected.map(
          (pk: number) => this.delegations.find((d) => d.id === pk)?.codDelegacionSol
        ) ?? undefined;
      this.params.tipoDocumento = this.filtersDocumentos.tipoDocumentoSelected ?? undefined;

      this.mostrarFiltros = false;

      this.getDocumentacion();
    }
  }
}
</script>
<style lang="scss" scoped>
.descripcion-cliente {
  margin-left: 10px;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  color: var(--secundario-prosegur-claro);
}

.check-icon {
  color: green;
}

.icon {
  width: 30px;
  color: var(--amarillo-prosegur);
  padding-left: 3px;
}

.filter_Documentacion {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (min-width: 681px) {
    float: right;
  }
}

.material::v-deep .vs__dropdown-toggle {
  border: none;
  cursor: pointer;
}

.material::v-deep .vs__selected-options .vs__selected {
  padding-left: 0;
  margin-top: 0;
  margin-left: 0;
  font-size: 14px;
  color: #808080;
  font-weight: bold;
}

#input-cod-cliente-button {
  width: 150px;
  font-size: 12px;
  padding: 0;
  color: #979797;
  text-align: start;
}

#input-cod-cliente-icon {
  margin-left: 8px;
}

#input-cod-cliente {
  width: 150px;
}

.separator {
  width: 1px;
  height: 100%;
  border: solid 1px #e9e9e9;
}

#divisas-group {
  padding: 0 2em;
}

#input-divisas-label {
  font-size: 12px;
  line-height: 1.33;
  color: #979797;
}

#divisas-group > button {
  margin-right: 10px;
  width: 68px;
  height: 23px;
  object-fit: contain;
  font-size: 12px;
  font-weight: 400;
  text-align: center;
  padding-top: 0.15rem;
  padding-left: 16px;
  padding-right: 16px;
  color: var(--secundario-prosegur-oscuro);
  background-color: var(--fondo-not-active-toggle);
  border-color: var(--fondo-not-active-toggle);
}

#divisas-group > button.active {
  font-weight: 500;
  background-color: var(--amarillo-prosegur);
  border-color: var(--amarillo-prosegur);
}
</style>
